import { useEffect, useState, useRef } from 'react';
import './App.css';

const ANALYTICS_API_URL = 'https://newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net';
// const ANALYTICS_API_URL = 'http://localhost:5002';

const fetchFromAPI = async (endpoint, options = {}) => {
  const token = localStorage.getItem('jwtToken');
  const headers = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${ANALYTICS_API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token is invalid or expired, clear it and reload
    localStorage.removeItem('jwtToken');
    window.location.reload();
    throw new Error('Session expired, please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message);
  }

  return response.json();
};

/**
 * Aggregation function for multi-day analytics data
 * 
 * This function takes an array of daily analytics objects and aggregates them into a single object.
 * It's designed to be modular and separate from the UI logic, so changes to aggregation logic
 * can be made here without affecting the page rendering.
 * 
 * Aggregation Logic:
 * - Numeric fields: Sum across all days
 * - Percentages: Weighted average based on totals
 * - Arrays: Merge and combine
 * - Objects: Deep merge with appropriate handling
 * - Lead time statistics: Recalculated from combined data
 * 
 * To modify aggregation behavior:
 * 1. Find the relevant section for the analytics type (booking, recall, SMS, etc.)
 * 2. Modify the aggregation logic for specific fields
 * 3. Test with sample data to ensure correctness
 * 
 * @param {Array} dataArray - Array of daily analytics objects from API
 * @returns {Object} - Single aggregated analytics object, or null if no data
 */
const aggregateAnalyticsData = (dataArray) => {
  try {
    if (!dataArray || dataArray.length === 0) {
      return null;
    }
    
    // If only one day, return as is
    if (dataArray.length === 1) {
      return dataArray[0];
    }
    
    // Initialize aggregated object with first item as template
    const aggregated = JSON.parse(JSON.stringify(dataArray[0]));
  
  // Update metadata for aggregated view
  aggregated.date = `${dataArray[0].date} to ${dataArray[dataArray.length - 1].date}`;
  aggregated.analysisTimestamp = new Date().toISOString();
  aggregated.id = `${aggregated.practiceId}_aggregated_${dataArray[0].date}_to_${dataArray[dataArray.length - 1].date}`;
  
  // Helper function to safely add numbers
  const safeAdd = (a, b) => (a || 0) + (b || 0);
  
  // Helper function to aggregate percentages (weighted average)
  const aggregatePercentage = (field, totalField) => {
    let totalValue = 0;
    let totalWeight = 0;
    
    dataArray.forEach(item => {
      const fieldPath = field.split('.');
      const totalFieldPath = totalField.split('.');
      
      let value = item;
      let total = item;
      
      for (const path of fieldPath) {
        value = value?.[path];
      }
      for (const path of totalFieldPath) {
        total = total?.[path];
      }
      
      if (typeof value === 'number' && typeof total === 'number' && total > 0) {
        totalValue += (value / 100) * total;
        totalWeight += total;
      }
    });
    
    return totalWeight > 0 ? (totalValue / totalWeight) * 100 : 0;
  };
  

  
  // Aggregate booking analytics
  if (aggregated.booking_analytics) {
    const bookingAnalytics = aggregated.booking_analytics;
    
    // Sum activity types
    Object.keys(bookingAnalytics.activity_types || {}).forEach(type => {
      bookingAnalytics.activity_types[type] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_analytics?.activity_types?.[type]), 0);
    });
    
    // Sum appointment types
    Object.keys(bookingAnalytics.appointment_types || {}).forEach(type => {
      bookingAnalytics.appointment_types[type] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_analytics?.appointment_types?.[type]), 0);
    });
    
    // Sum booking outcomes
    Object.keys(bookingAnalytics.booking_outcomes || {}).forEach(outcome => {
      bookingAnalytics.booking_outcomes[outcome] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_analytics?.booking_outcomes?.[outcome]), 0);
    });
    
    // Sum booking types
    Object.keys(bookingAnalytics.booking_types || {}).forEach(type => {
      if (type.includes('percentage')) {
        // For percentages, calculate weighted average
        if (type === 'recall_percentage') {
          bookingAnalytics.booking_types[type] = aggregatePercentage('booking_analytics.booking_types.recall_percentage', 'recall_call_analytics.exact_results.bookings_made');
        } else if (type === 'inbound_percentage') {
          bookingAnalytics.booking_types[type] = aggregatePercentage('booking_analytics.booking_types.inbound_percentage', 'recall_call_analytics.exact_results.bookings_made');
        } else if (type === 'non_recall_percentage') {
          bookingAnalytics.booking_types[type] = aggregatePercentage('booking_analytics.booking_types.non_recall_percentage', 'recall_call_analytics.exact_results.bookings_made');
        }
      } else {
        bookingAnalytics.booking_types[type] = dataArray.reduce((sum, item) => 
          safeAdd(sum, item.booking_analytics?.booking_types?.[type]), 0);
      }
    });
    
    // Sum channels
    Object.keys(bookingAnalytics.channels || {}).forEach(channel => {
      bookingAnalytics.channels[channel] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_analytics?.channels?.[channel]), 0);
    });
    
    // Sum scheduling events
    Object.keys(bookingAnalytics.scheduling_events || {}).forEach(event => {
      bookingAnalytics.scheduling_events[event] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_analytics?.scheduling_events?.[event]), 0);
    });
    
    // Sum summary fields
    Object.keys(bookingAnalytics.summary || {}).forEach(field => {
      if (field === 'success_rate' || field === 'failure_rate') {
        // Calculate weighted average for rates
        if (field === 'success_rate') {
          // Success rate = bookings made / pickups
          const totalBookings = dataArray.reduce((sum, item) => safeAdd(sum, item.recall_call_analytics?.exact_results?.bookings_made), 0);
          const totalPickups = dataArray.reduce((sum, item) => safeAdd(sum, item.recall_call_analytics?.pickups), 0);
          bookingAnalytics.summary[field] = totalPickups > 0 ? (totalBookings / totalPickups) * 100 : 0;
        } else if (field === 'failure_rate') {
          // Failure rate = 100% - success rate
          const totalBookings = dataArray.reduce((sum, item) => safeAdd(sum, item.recall_call_analytics?.exact_results?.bookings_made), 0);
          const totalPickups = dataArray.reduce((sum, item) => safeAdd(sum, item.recall_call_analytics?.pickups), 0);
          const successRate = totalPickups > 0 ? (totalBookings / totalPickups) * 100 : 0;
          bookingAnalytics.summary[field] = 100 - successRate;
        }
      } else {
        bookingAnalytics.summary[field] = dataArray.reduce((sum, item) => 
          safeAdd(sum, item.booking_analytics?.summary?.[field]), 0);
      }
    });
    
    // Sum summary patterns
    Object.keys(bookingAnalytics.summary_patterns || {}).forEach(pattern => {
      bookingAnalytics.summary_patterns[pattern] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_analytics?.summary_patterns?.[pattern]), 0);
    });
    
    // Sum other booking fields - use recallAnalytics.exact_results.bookings_made for total bookings
    bookingAnalytics.total_bookings = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.exact_results?.bookings_made), 0);
    bookingAnalytics.recall_bookings = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.booking_analytics?.recall_bookings), 0);
    bookingAnalytics.non_recall_bookings = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.booking_analytics?.non_recall_bookings), 0);
    
    // Recalculate percentages
    if (bookingAnalytics.total_bookings > 0) {
      bookingAnalytics.recall_percentage = (bookingAnalytics.recall_bookings / bookingAnalytics.total_bookings) * 100;
      bookingAnalytics.non_recall_percentage = (bookingAnalytics.non_recall_bookings / bookingAnalytics.total_bookings) * 100;
    }
  }
  
  // Aggregate recall call analytics
  if (aggregated.recall_call_analytics) {
    const recallAnalytics = aggregated.recall_call_analytics;
    
    // Sum basic metrics
    recallAnalytics.totalRecallCalls = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.totalRecallCalls), 0);
    recallAnalytics.pickups = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.pickups), 0);
    recallAnalytics.voicemails = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.voicemails), 0);
    recallAnalytics.not_connected = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.not_connected), 0);
    
    // Sum exact results
    Object.keys(recallAnalytics.exact_results || {}).forEach(result => {
      recallAnalytics.exact_results[result] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.recall_call_analytics?.exact_results?.[result]), 0);
    });
    
    // Sum tasks
    recallAnalytics.tasks.total_tasks_created = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.tasks?.total_tasks_created), 0);
    recallAnalytics.tasks.follow_up_texts = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.recall_call_analytics?.tasks?.follow_up_texts), 0);
    
    Object.keys(recallAnalytics.tasks.tasks_by_priority || {}).forEach(priority => {
      recallAnalytics.tasks.tasks_by_priority[priority] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.recall_call_analytics?.tasks?.tasks_by_priority?.[priority]), 0);
    });
    
    // Aggregate judge data
    Object.keys(recallAnalytics.judge || {}).forEach(field => {
      if (field === 'reasons' || field === 'reasonsForNotBooking') {
        // Merge reasons objects
        const mergedReasons = {};
        dataArray.forEach(item => {
          const reasons = item.recall_call_analytics?.judge?.[field] || {};
          Object.keys(reasons).forEach(reason => {
            if (!mergedReasons[reason]) {
              mergedReasons[reason] = { count: 0, call_ids: [] };
            }
            mergedReasons[reason].count += reasons[reason].count || 0;
            if (reasons[reason].call_ids) {
              mergedReasons[reason].call_ids.push(...reasons[reason].call_ids);
            }
          });
        });
        recallAnalytics.judge[field] = mergedReasons;
      } else {
        recallAnalytics.judge[field] = dataArray.reduce((sum, item) => 
          safeAdd(sum, item.recall_call_analytics?.judge?.[field]), 0);
      }
    });
    
    // Merge categorized phone numbers
    recallAnalytics.categorized_phone_numbers = {
      book_later_numbers: [],
      no_pickup_numbers: []
    };
    dataArray.forEach(item => {
      const phoneNumbers = item.recall_call_analytics?.categorized_phone_numbers;
      if (phoneNumbers?.book_later_numbers) {
        recallAnalytics.categorized_phone_numbers.book_later_numbers.push(...phoneNumbers.book_later_numbers);
      }
      if (phoneNumbers?.no_pickup_numbers) {
        recallAnalytics.categorized_phone_numbers.no_pickup_numbers.push(...phoneNumbers.no_pickup_numbers);
      }
    });
  }
  
  // Aggregate patient SMS outreach analytics
  if (aggregated.patient_sms_outreach_analytics) {
    const smsAnalytics = aggregated.patient_sms_outreach_analytics;
    
    // Sum numeric fields
    const smsFields = [
      'total_conversations_checked', 'total_conversations_with_outreach', 'total_outreach',
      'total_sms_sent', 'total_system_messages_sent', 'total_patient_responses_received',
      'total_staff_responses_received', 'total_user_responses_received', 'total_activities_generated',
      'conversations_with_activities', 'conversations_with_user_responses',
      'unique_patient_names_reached', 'unique_phone_numbers_reached', 'unique_responding_phone_numbers'
    ];
    
    smsFields.forEach(field => {
      smsAnalytics[field] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.patient_sms_outreach_analytics?.[field]), 0);
    });
    
    // Calculate response rate
    if (smsAnalytics.total_conversations_with_outreach > 0) {
      smsAnalytics.response_rate_percentage = (smsAnalytics.conversations_with_user_responses / smsAnalytics.total_conversations_with_outreach) * 100;
    } else {
      smsAnalytics.response_rate_percentage = 0;
    }
    
    // Calculate activity generation rate
    if (smsAnalytics.total_conversations_with_outreach > 0) {
      smsAnalytics.activity_generation_rate = (smsAnalytics.conversations_with_activities / smsAnalytics.total_conversations_with_outreach) * 100;
    } else {
      smsAnalytics.activity_generation_rate = 0;
    }
    
    // Merge conversation details samples (limit to 10 for display)
    smsAnalytics.conversation_details_sample = [];
    dataArray.forEach(item => {
      const samples = item.patient_sms_outreach_analytics?.conversation_details_sample || [];
      smsAnalytics.conversation_details_sample.push(...samples);
    });
    smsAnalytics.conversation_details_sample = smsAnalytics.conversation_details_sample.slice(0, 10);
  }
  
  // Aggregate callback analytics
  if (aggregated.callback_analytics) {
    const callbackAnalytics = aggregated.callback_analytics;
    
    // Sum no pickup analysis
    callbackAnalytics.no_pickup_analysis.total_no_pickup = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.callback_analytics?.no_pickup_analysis?.total_no_pickup), 0);
    callbackAnalytics.no_pickup_analysis.callbacks_received = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.callback_analytics?.no_pickup_analysis?.callbacks_received), 0);
    
    // Calculate callback rate for no pickup
    if (callbackAnalytics.no_pickup_analysis.total_no_pickup > 0) {
      callbackAnalytics.no_pickup_analysis.callback_rate_percentage = 
        (callbackAnalytics.no_pickup_analysis.callbacks_received / callbackAnalytics.no_pickup_analysis.total_no_pickup) * 100;
    } else {
      callbackAnalytics.no_pickup_analysis.callback_rate_percentage = 0;
    }
    
    // Sum book later analysis
    callbackAnalytics.book_later_analysis.total_book_later = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.callback_analytics?.book_later_analysis?.total_book_later), 0);
    callbackAnalytics.book_later_analysis.callbacks_received = dataArray.reduce((sum, item) => 
      safeAdd(sum, item.callback_analytics?.book_later_analysis?.callbacks_received), 0);
    
    // Calculate callback rate for book later
    if (callbackAnalytics.book_later_analysis.total_book_later > 0) {
      callbackAnalytics.book_later_analysis.callback_rate_percentage = 
        (callbackAnalytics.book_later_analysis.callbacks_received / callbackAnalytics.book_later_analysis.total_book_later) * 100;
    } else {
      callbackAnalytics.book_later_analysis.callback_rate_percentage = 0;
    }
  }
  
  // Aggregate booking lead time analytics
  if (aggregated.booking_lead_time_analytics) {
    const leadTimeAnalytics = aggregated.booking_lead_time_analytics;
    
    // Sum distribution
    Object.keys(leadTimeAnalytics.distribution || {}).forEach(period => {
      leadTimeAnalytics.distribution[period] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.booking_lead_time_analytics?.distribution?.[period]), 0);
    });
    
    // Calculate aggregated statistics
    const leadTimes = [];
    let totalProcessed = 0;
    let totalErrors = 0;
    
    dataArray.forEach(item => {
      const analytics = item.booking_lead_time_analytics;
      if (analytics) {
        totalProcessed += analytics.processed_count || 0;
        totalErrors += analytics.error_count || 0;
        
        // Collect individual lead times if available (would need raw data)
        // For now, use mean values weighted by processed count
        if (analytics.processed_count > 0 && analytics.mean_lead_time > 0) {
          for (let i = 0; i < analytics.processed_count; i++) {
            leadTimes.push(analytics.mean_lead_time);
          }
        }
      }
    });
    
    leadTimeAnalytics.processed_count = totalProcessed;
    leadTimeAnalytics.error_count = totalErrors;
    leadTimeAnalytics.valid_calculations = leadTimes.length;
    
    if (leadTimes.length > 0) {
      leadTimes.sort((a, b) => a - b);
      leadTimeAnalytics.mean_lead_time = leadTimes.reduce((sum, val) => sum + val, 0) / leadTimes.length;
      leadTimeAnalytics.median_lead_time = leadTimes[Math.floor(leadTimes.length / 2)];
      leadTimeAnalytics.min_lead_time = Math.min(...leadTimes);
      leadTimeAnalytics.max_lead_time = Math.max(...leadTimes);
      leadTimeAnalytics.p25_lead_time = leadTimes[Math.floor(leadTimes.length * 0.25)];
      leadTimeAnalytics.p75_lead_time = leadTimes[Math.floor(leadTimes.length * 0.75)];
      
      // Calculate standard deviation
      const mean = leadTimeAnalytics.mean_lead_time;
      const variance = leadTimes.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / leadTimes.length;
      leadTimeAnalytics.std_dev = Math.sqrt(variance);
    } else {
      leadTimeAnalytics.mean_lead_time = 0;
      leadTimeAnalytics.median_lead_time = 0;
      leadTimeAnalytics.min_lead_time = 0;
      leadTimeAnalytics.max_lead_time = 0;
      leadTimeAnalytics.p25_lead_time = 0;
      leadTimeAnalytics.p75_lead_time = 0;
      leadTimeAnalytics.std_dev = 0;
    }
  }
  
  // Aggregate recall no show analytics
  if (aggregated.recall_no_show_analytics) {
    const noShowAnalytics = aggregated.recall_no_show_analytics;
    
    const fields = [
      'total_appointments_checked', 'pending_appointments', 'confirmed_appointments',
      'completed_appointments', 'no_show_appointments', 'cancelled_appointments',
      'calls_with_bookings', 'total_recall_calls', 'denticon_appointments_checked',
      'kolla_appointments_checked', 'unknown_pms_appointments', 'unknown_status_appointments'
    ];
    
    fields.forEach(field => {
      noShowAnalytics[field] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.recall_no_show_analytics?.[field]), 0);
    });
    
    // Calculate rates
    if (noShowAnalytics.total_appointments_checked > 0) {
      noShowAnalytics.no_show_rate = (noShowAnalytics.no_show_appointments / noShowAnalytics.total_appointments_checked) * 100;
      noShowAnalytics.cancellation_rate = (noShowAnalytics.cancelled_appointments / noShowAnalytics.total_appointments_checked) * 100;
      noShowAnalytics.completion_rate = (noShowAnalytics.completed_appointments / noShowAnalytics.total_appointments_checked) * 100;
    } else {
      noShowAnalytics.no_show_rate = 0;
      noShowAnalytics.cancellation_rate = 0;
      noShowAnalytics.completion_rate = 0;
    }
  }
  
  // Aggregate days since last appointment analytics
  if (aggregated.days_since_last_appointment_analytics) {
    const daysAnalytics = aggregated.days_since_last_appointment_analytics;
    
    const fields = [
      'matched_outreach_records', 'calls_with_bookings', 'total_recall_calls',
      'error_count', 'total_days_call_to_last', 'total_days_booking_to_last'
    ];
    
    fields.forEach(field => {
      daysAnalytics[field] = dataArray.reduce((sum, item) => 
        safeAdd(sum, item.days_since_last_appointment_analytics?.[field]), 0);
    });
    
    // Calculate averages
    if (daysAnalytics.calls_with_bookings > 0) {
      daysAnalytics.average_days_booking_to_last_appointment = 
        daysAnalytics.total_days_booking_to_last / daysAnalytics.calls_with_bookings;
    } else {
      daysAnalytics.average_days_booking_to_last_appointment = 0;
    }
    
    if (daysAnalytics.total_recall_calls > 0) {
      daysAnalytics.average_days_call_to_last_appointment = 
        daysAnalytics.total_days_call_to_last / daysAnalytics.total_recall_calls;
    } else {
      daysAnalytics.average_days_call_to_last_appointment = 0;
    }
  }
  
  return aggregated;
  
  } catch (error) {
    console.error('Error aggregating analytics data:', error);
    // Return first item as fallback if aggregation fails
    return dataArray && dataArray.length > 0 ? dataArray[0] : null;
  }
};

/* 
// Test function for aggregation (remove from production)
const testAggregation = () => {
  const sampleData = [
    {
      date: "2025-08-21",
      practiceId: "practice-1739995683291",
      booking_analytics: {
        total_bookings: 2,
        recall_bookings: 0,
        booking_outcomes: { successful: 2, failed: 0 }
      },
      recall_call_analytics: {
        totalRecallCalls: 7,
        pickups: 5,
        exact_results: { bookings_made: 0 }
      }
    },
    {
      date: "2025-08-22", 
      practiceId: "practice-1739995683291",
      booking_analytics: {
        total_bookings: 1,
        recall_bookings: 0,
        booking_outcomes: { successful: 1, failed: 0 }
      },
      recall_call_analytics: {
        totalRecallCalls: 7,
        pickups: 1,
        exact_results: { bookings_made: 0 }
      }
    }
  ];
  
  const result = aggregateAnalyticsData(sampleData);
  console.log('Aggregated result:', result);
  // Expected: total_bookings: 3, totalRecallCalls: 14, pickups: 6
};
*/

const OutboundAnalytics = () => {
  // State for analytics data
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dailyData, setDailyData] = useState(null); // Store raw daily data for timeline
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for practices list
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(false);
  const [practicesError, setPracticesError] = useState('');
  
  // Date range states
  const [practiceId, setPracticeId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    // Set to 7 days ago as default (YYYY-MM-DD format)
    const date = new Date();
    date.setDate(date.getDate() - 7);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDate, setEndDate] = useState(() => {
    // Set current local date as default (YYYY-MM-DD format)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  
  // Date range picker UI states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  
  // Ref for click-outside functionality
  const datePickerRef = useRef(null);

  // Fetch practices on component mount
  useEffect(() => {
    const fetchPractices = async () => {
      setPracticesLoading(true);
      setPracticesError('');
      
      try {
        const response = await fetchFromAPI('/api/practices/live');
        
        if (response.success && response.data) {
          setPractices(response.data);
          console.log(`Loaded ${response.data.length} live practices`);
        } else {
          setPracticesError('Failed to load practices');
        }
      } catch (error) {
        console.error('Error fetching practices:', error);
        setPracticesError(`Failed to load practices: ${error.message}`);
      } finally {
        setPracticesLoading(false);
      }
    };
    
    fetchPractices();
  }, []);

  // Click-outside handler for date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Fetch analytics data for specific practice and date range
  const fetchAnalyticsData = async () => {
    if (!practiceId.trim()) {
      setError('Please enter a Practice ID.');
      return;
    }
    
    if (!startDate || !endDate) {
      setError('Please select start and end dates.');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        practice_id: practiceId,
        start_date: startDate,
        end_date: endDate
      });
      
      const response = await fetchFromAPI(`/api/outbound/analytics?${params.toString()}`);
      
      // Handle array response - aggregate all days in date range
      if (response.success && response.data && response.data.length > 0) {
        const aggregatedData = aggregateAnalyticsData(response.data);
        setAnalyticsData(aggregatedData);
        setDailyData(response.data); // Store raw daily data for timeline
      } else {
        setError('No data found for the selected date range.');
        setAnalyticsData(null);
        setDailyData(null);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError(`Failed to load analytics data: ${error.message}`);
      setAnalyticsData(null);
      setDailyData(null);
    } finally {
      setLoading(false);
    }
  };

  // Clear data
  const clearData = () => {
    setPracticeId('');
    
    // Reset to last 7 days
    const endDateObj = new Date();
    const endYear = endDateObj.getFullYear();
    const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
    const endDay = String(endDateObj.getDate()).padStart(2, '0');
    const endDateString = `${endYear}-${endMonth}-${endDay}`;
    
    const startDateObj = new Date();
    startDateObj.setDate(startDateObj.getDate() - 7);
    const startYear = startDateObj.getFullYear();
    const startMonth = String(startDateObj.getMonth() + 1).padStart(2, '0');
    const startDay = String(startDateObj.getDate()).padStart(2, '0');
    const startDateString = `${startYear}-${startMonth}-${startDay}`;
    
    setStartDate(startDateString);
    setEndDate(endDateString);
    setAnalyticsData(null);
    setDailyData(null);
    setError('');
    setShowDatePicker(false);
    setSelectingStart(true);
    setCurrentMonth(new Date());
    setHoveredDate(null);
  };

  // Date range picker helpers
  const formatDateRange = () => {
    if (!startDate || !endDate) {
      return 'Select dates';
    }
    
    // Parse dates safely (YYYY-MM-DD format)
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    
    const startDateObj = parseDate(startDate);
    const endDateObj = parseDate(endDate);
    
    if (startDate === endDate) {
      return startDateObj.toLocaleDateString();
    }
    return `${startDateObj.toLocaleDateString()} - ${endDateObj.toLocaleDateString()}`;
  };

  const openDatePicker = () => {
    setSelectingStart(true);
    setShowDatePicker(true);
  };

  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (date) => {
    const dateString = formatDateToString(date);
    
    if (selectingStart) {
      setStartDate(dateString);
      setEndDate(dateString); // Set end date to same initially
      setSelectingStart(false);
    } else {
      if (new Date(dateString) >= new Date(startDate)) {
        setEndDate(dateString);
        setShowDatePicker(false);
        setSelectingStart(true);
      } else {
        // If selected date is before start date, make it the new start date
        setStartDate(dateString);
        setEndDate(startDate);
        setShowDatePicker(false);
        setSelectingStart(true);
      }
    }
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    
    setStartDate(formatDateToString(start));
    setEndDate(formatDateToString(end));
    setShowDatePicker(false);
    setSelectingStart(true);
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDateToString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isDateRangeHover = (date) => {
    if (!hoveredDate || !startDate || selectingStart) return false;
    const dateStr = formatDateToString(date);
    const startStr = startDate;
    const hoverStr = formatDateToString(hoveredDate);
    
    if (hoverStr >= startStr) {
      return dateStr >= startStr && dateStr <= hoverStr;
    } else {
      return dateStr >= hoverStr && dateStr <= startStr;
    }
  };

  const isStartDate = (date) => {
    return startDate === formatDateToString(date);
  };

  const isEndDate = (date) => {
    return endDate === formatDateToString(date) && startDate !== endDate;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      return timestamp;
    }
  };





  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${Number(value).toFixed(2)}%`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return value.toLocaleString();
  };

  // Extract key metrics from daily data for timeline chart
  const extractTimelineData = (dailyDataArray) => {
    if (!dailyDataArray || dailyDataArray.length === 0) return [];
    
    return dailyDataArray.map(dayData => {
      const recallAnalytics = dayData.recall_call_analytics || {};
      const smsAnalytics = dayData.patient_sms_outreach_analytics || {};
      const callbackAnalytics = dayData.callback_analytics || {};
      
      const totalCalls = recallAnalytics.totalRecallCalls || 0;
      const pickups = recallAnalytics.pickups || 0;
      const bookingsMade = recallAnalytics.exact_results?.bookings_made || 0;
      const totalBookings = recallAnalytics.exact_results?.bookings_made || 0;
      const pickupRate = totalCalls > 0 ? (pickups / totalCalls) * 100 : 0;
      // Calculate booking success rate for individual daily entries: bookings made / pickups
      const bookingSuccessRate = pickups > 0 ? (bookingsMade / pickups) * 100 : 0;
      const smsResponseRate = smsAnalytics.response_rate_percentage || 0;
      
      // Calculate overall callback rate
      const totalCallbackOpportunities = (callbackAnalytics.no_pickup_analysis?.total_no_pickup || 0) + (callbackAnalytics.book_later_analysis?.total_book_later || 0);
      const totalCallbacks = (callbackAnalytics.no_pickup_analysis?.callbacks_received || 0) + (callbackAnalytics.book_later_analysis?.callbacks_received || 0);
      const callbackRate = totalCallbackOpportunities > 0 ? (totalCallbacks / totalCallbackOpportunities) * 100 : 0;
      
      return {
        date: dayData.date,
        totalCalls,
        pickups,
        bookingsMade,
        totalBookings,
        pickupRate,
        bookingSuccessRate,
        smsResponseRate,
        callbackRate,
        voicemails: recallAnalytics.voicemails || 0,
        notConnected: recallAnalytics.not_connected || 0
      };
    }).sort((a, b) => {
      // Parse dates as local time to avoid UTC timezone issues
      const parseLocalDate = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      };
      return parseLocalDate(a.date) - parseLocalDate(b.date);
    }); // Sort by date
  };



  // State for metric toggles
  const [activeMetrics, setActiveMetrics] = useState({
    totalCalls: true,
    pickups: true,
    totalBookings: false,
    pickupRate: false,
    bookingSuccessRate: false,
    callbackRate: false
  });
  
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Toggle metric visibility
  const toggleMetric = (metricKey) => {
    setActiveMetrics(prev => ({
      ...prev,
      [metricKey]: !prev[metricKey]
    }));
  };

  // Optimized hover handlers
  const handleMouseEnter = (index, point) => {
    setHoveredPoint({ index, point });
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  // Google Search Console style timeline chart
  const renderTimelineChart = () => {
    if (!dailyData || dailyData.length === 0) return null;
    
    const timelineData = extractTimelineData(dailyData);
    if (timelineData.length === 0) return null;
    
    // Define all available metrics
    const allMetrics = [
      { key: 'totalCalls', label: 'Total Calls', color: '#4285f4', type: 'count' },
      { key: 'pickups', label: 'Pickups', color: '#34a853', type: 'count' },
      { key: 'totalBookings', label: 'Total Bookings', color: '#fbbc04', type: 'count' },
      { key: 'pickupRate', label: 'Pickup Rate', color: '#ea4335', type: 'percentage' },
      { key: 'bookingSuccessRate', label: 'Booking Success Rate', color: '#9c27b0', type: 'percentage' },
      { key: 'callbackRate', label: 'Callback Rate', color: '#00bcd4', type: 'percentage' }
    ];
    
    // Get only active metrics
    const activeMetricsList = allMetrics.filter(metric => activeMetrics[metric.key]);
    
    // Calculate scales for each type
    const getScale = (type) => {
      const relevantMetrics = activeMetricsList.filter(m => m.type === type);
      if (relevantMetrics.length === 0) return { min: 0, max: 100 };
      
      let allValues = [];
      relevantMetrics.forEach(metric => {
        timelineData.forEach(point => {
          allValues.push(point[metric.key] || 0);
        });
      });
      
      const max = Math.max(...allValues);
      
      if (type === 'percentage') {
        return { min: 0, max: Math.max(100, max * 1.1) };
      } else {
        return { min: 0, max: max * 1.1 };
      }
    };
    
    const countScale = getScale('count');
    const percentageScale = getScale('percentage');
    
    // Chart dimensions - responsive
    const chartWidth = 800; // Fixed width for viewBox, SVG will scale
    const chartHeight = 300;
    const padding = { top: 20, right: 60, bottom: 50, left: 60 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;
    
    // Create coordinate functions - handle single point case
    const xScale = (index) => {
      if (timelineData.length === 1) {
        return plotWidth / 2; // Center single point
      }
      return (index / (timelineData.length - 1)) * plotWidth;
    };
    const yScale = (value, type) => {
      const scale = type === 'percentage' ? percentageScale : countScale;
      return plotHeight - ((value - scale.min) / (scale.max - scale.min)) * plotHeight;
    };
    
    // Generate path for a metric line
    const generatePath = (metric) => {
      if (timelineData.length === 1) {
        // For single point, don't draw a line
        return '';
      }
      const points = timelineData.map((point, index) => {
        const x = xScale(index);
        const y = yScale(point[metric.key] || 0, metric.type);
        return `${x},${y}`;
      });
      return `M ${points.join(' L ')}`;
    };
    
    // Format date for display - parse as local time
    const formatDateShort = (dateStr) => {
      // Parse as local date to avoid UTC timezone issues
      const [year, month, day] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    // Format day of week for display - parse as local time
    const formatDayOfWeek = (dateStr) => {
      // Parse as local date to avoid UTC timezone issues
      const [year, month, day] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    };
    
    // Format value for display
    const formatValue = (value, type) => {
      if (type === 'percentage') {
        return `${value.toFixed(1)}%`;
      } else {
        return formatNumber(Math.round(value));
      }
    };
    
    return (
      <div className="gsc-chart-section">
        <h3 className="section-title">Performance Over Time</h3>
        
        {/* Metric Toggle Buttons */}
        <div className="metric-toggles">
          {allMetrics.map(metric => (
            <button
              key={metric.key}
              className={`metric-toggle ${activeMetrics[metric.key] ? 'active' : ''}`}
              onClick={() => toggleMetric(metric.key)}
              style={{
                '--metric-color': metric.color
              }}
            >
              <span className="metric-indicator"></span>
              {metric.label}
            </button>
          ))}
        </div>
        
        {/* Chart Container */}
        <div className="gsc-chart-container">
          <svg 
            width="100%" 
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="gsc-chart-svg"
            onMouseLeave={handleMouseLeave}
          >
            {/* Grid lines */}
            <defs>
              <pattern id="grid" width="1" height="20" patternUnits="userSpaceOnUse">
                <path d="M 1 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
              </pattern>
            </defs>
            
            {/* Y-axis grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
              const y = padding.top + ratio * plotHeight;
              return (
                <line
                  key={ratio}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + plotWidth}
                  y2={y}
                  stroke="#f0f0f0"
                  strokeWidth="1"
                />
              );
            })}
            
            {/* X-axis grid lines */}
            {timelineData.map((_, index) => {
              if (index % Math.max(1, Math.floor(timelineData.length / 6)) !== 0) return null;
              const x = padding.left + xScale(index);
              return (
                <line
                  key={index}
                  x1={x}
                  y1={padding.top}
                  x2={x}
                  y2={padding.top + plotHeight}
                  stroke="#f0f0f0"
                  strokeWidth="1"
                />
              );
            })}
            
            {/* Metric lines */}
            {activeMetricsList.map(metric => (
              <path
                key={metric.key}
                d={generatePath(metric)}
                fill="none"
                stroke={metric.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                transform={`translate(${padding.left}, ${padding.top})`}
                className="metric-line"
              />
            ))}
            
            {/* Invisible hover areas for better performance */}
            {timelineData.map((point, index) => (
              <rect
                key={`hover-${index}`}
                x={padding.left + xScale(index) - 40}
                y={padding.top - 20}
                width="80"
                height={plotHeight + 40}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => handleMouseEnter(index, point)}
                onMouseLeave={handleMouseLeave}
              />
            ))}
            
            {/* Data points */}
            {activeMetricsList.map(metric => 
              timelineData.map((point, index) => (
                <circle
                  key={`${metric.key}-${index}`}
                  cx={padding.left + xScale(index)}
                  cy={padding.top + yScale(point[metric.key] || 0, metric.type)}
                  r={timelineData.length === 1 ? "8" : "4"}
                  fill={metric.color}
                  stroke="white"
                  strokeWidth="2"
                  className="data-point"
                  style={{ pointerEvents: 'none' }}
                />
              ))
            )}
            
            {/* Hover line */}
            {hoveredPoint && (
              <line
                x1={padding.left + xScale(hoveredPoint.index)}
                y1={padding.top}
                x2={padding.left + xScale(hoveredPoint.index)}
                y2={padding.top + plotHeight}
                stroke="#666"
                strokeWidth="1"
                strokeDasharray="4,4"
                className="hover-line"
              />
            )}
            
            {/* X-axis labels */}
            {timelineData.map((point, index) => {
              if (index % Math.max(1, Math.floor(timelineData.length / 6)) !== 0) return null;
              return (
                <g key={index}>
                  <text
                    x={padding.left + xScale(index)}
                    y={chartHeight - 25}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#666"
                  >
                    {formatDateShort(point.date)}
                  </text>
                  <text
                    x={padding.left + xScale(index)}
                    y={chartHeight - 10}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#999"
                  >
                    {formatDayOfWeek(point.date)}
                  </text>
                </g>
              );
            })}
            
            {/* Y-axis labels - Count scale (left) */}
            {activeMetricsList.some(m => m.type === 'count') && [0, 0.25, 0.5, 0.75, 1].map(ratio => {
              const value = countScale.min + ratio * (countScale.max - countScale.min);
              const y = padding.top + (1 - ratio) * plotHeight;
              return (
                <text
                  key={`count-${ratio}`}
                  x={padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                >
                  {Math.round(value)}
                </text>
              );
            })}
            
            {/* Y-axis labels - Percentage scale (right) */}
            {activeMetricsList.some(m => m.type === 'percentage') && [0, 0.25, 0.5, 0.75, 1].map(ratio => {
              const value = percentageScale.min + ratio * (percentageScale.max - percentageScale.min);
              const y = padding.top + (1 - ratio) * plotHeight;
              return (
                <text
                  key={`percentage-${ratio}`}
                  x={chartWidth - padding.right + 10}
                  y={y + 4}
                  textAnchor="start"
                  fontSize="12"
                  fill="#666"
                >
                  {value.toFixed(0)}%
                </text>
              );
            })}
          </svg>
          
          {/* Tooltip */}
          {hoveredPoint && (
            <div 
              className="chart-tooltip" 
              style={{
                left: `${Math.min(Math.max((hoveredPoint.index / (timelineData.length - 1)) * 100, 10), 90)}%`
              }}
            >
              <div className="tooltip-date">
                {formatDayOfWeek(hoveredPoint.point.date)}, {formatDateShort(hoveredPoint.point.date)}
              </div>
              {activeMetricsList.map(metric => (
                <div key={metric.key} className="tooltip-metric">
                  <span className="tooltip-color" style={{ backgroundColor: metric.color }}></span>
                  <span className="tooltip-label">{metric.label}:</span>
                  <span className="tooltip-value">
                    {formatValue(hoveredPoint.point[metric.key] || 0, metric.type)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render summary KPI cards
  const renderSummaryCards = () => {
    if (!analyticsData) return null;

    const recallAnalytics = analyticsData.recall_call_analytics || {};
    const smsAnalytics = analyticsData.patient_sms_outreach_analytics || {};
    const leadTimeAnalytics = analyticsData.booking_lead_time_analytics || {};
    const callbackAnalytics = analyticsData.callback_analytics || {};
    
    const totalBookings = recallAnalytics.exact_results?.bookings_made || 0;
    const totalCalls = recallAnalytics.totalRecallCalls || 0;
    const totalPickups = recallAnalytics.pickups || 0;
    const pickupRate = totalCalls > 0 ? Number(((totalPickups / totalCalls) * 100).toFixed(2)) : 0;
    const bookingSuccessRate = totalPickups > 0 ? Number(((totalBookings / totalPickups) * 100).toFixed(2)) : 0;
    const avgLeadTime = leadTimeAnalytics.mean_lead_time || 0;
    const smsResponseRate = smsAnalytics.response_rate_percentage || 0;
    
    // Calculate overall callback rate
    const totalCallbackOpportunities = (callbackAnalytics.no_pickup_analysis?.total_no_pickup || 0) + (callbackAnalytics.book_later_analysis?.total_book_later || 0);
    const totalCallbacks = (callbackAnalytics.no_pickup_analysis?.callbacks_received || 0) + (callbackAnalytics.book_later_analysis?.callbacks_received || 0);
    const overallCallbackRate = totalCallbackOpportunities > 0 ? Number(((totalCallbacks / totalCallbackOpportunities) * 100).toFixed(2)) : 0;

    return (
      <div className="analytics-summary-cards">
        <div className="summary-card primary">
          <div className="card-icon">📞</div>
          <div className="card-content">
            <div className="card-title">Total Calls</div>
            <div className="card-value">{formatNumber(totalCalls)}</div>
            <div className="card-subtitle">{recallAnalytics.pickups || 0} pickups</div>
          </div>
        </div>
        
        <div className="summary-card success">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <div className="card-title">Bookings Made</div>
            <div className="card-value">{formatNumber(totalBookings)}</div>
            <div className="card-subtitle">{formatPercentage(bookingSuccessRate)} success rate</div>
          </div>
        </div>
        
        <div className="summary-card info">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <div className="card-title">Pickup Rate</div>
            <div className="card-value">{formatPercentage(pickupRate)}</div>
            <div className="card-subtitle">of total calls</div>
          </div>
        </div>
        
        <div className="summary-card warning">
          <div className="card-icon">⏰</div>
          <div className="card-content">
            <div className="card-title">Avg Lead Time</div>
            <div className="card-value">{Number(avgLeadTime).toFixed(2)} days</div>
            <div className="card-subtitle">booking to appointment</div>
          </div>
        </div>
        
        <div className="summary-card secondary">
          <div className="card-icon">💬</div>
          <div className="card-content">
            <div className="card-title">SMS Response</div>
            <div className="card-value">{formatPercentage(smsResponseRate)}</div>
            <div className="card-subtitle">{smsAnalytics.total_conversations_checked || 0} conversations</div>
          </div>
        </div>
        
        <div className="summary-card info">
          <div className="card-icon">🔄</div>
          <div className="card-content">
            <div className="card-title">Callback Rate</div>
            <div className="card-value">{formatPercentage(overallCallbackRate)}</div>
            <div className="card-subtitle">{totalCallbacks} of {totalCallbackOpportunities} opportunities</div>
          </div>
        </div>
      </div>
    );
  };

  // Render booking analytics section
  const renderBookingAnalytics = () => {
    if (!analyticsData?.booking_analytics) return null;
    
    const data = analyticsData.booking_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Booking Analytics</h3>
        
        <div className="analytics-grid">
          {/* Booking Outcomes */}
          <div className="analytics-card">
            <h4>Booking Success</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Successful:</span>
                <span className="metric-value success">{data.booking_outcomes?.successful || 0}</span>
              </div>
              <div className="metric-row">
                <span>Failed:</span>
                <span className="metric-value error">{data.booking_outcomes?.failed || 0}</span>
              </div>
              <div className="metric-row">
                <span>Unknown:</span>
                <span className="metric-value neutral">{data.booking_outcomes?.unknown || 0}</span>
              </div>
            </div>
          </div>
          
          {/* Activity Types */}
          <div className="analytics-card">
            <h4>Activity Types</h4>
            <div className="chart-placeholder">
              {Object.entries(data.activity_types || {}).map(([type, count]) => (
                <div key={type} className="metric-row">
                  <span>{type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                  <span className="metric-value">{count}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Appointment Types */}
          <div className="analytics-card">
            <h4>Appointment Types</h4>
            <div className="chart-placeholder">
              {Object.entries(data.appointment_types || {}).map(([type, count]) => (
                <div key={type} className="metric-row">
                  <span>{type}:</span>
                  <span className="metric-value">{count}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Booking Types */}
          <div className="analytics-card">
            <h4>Booking Distribution</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Recall Bookings:</span>
                <span className="metric-value primary">{data.booking_types?.recall_bookings || 0}</span>
              </div>
              <div className="metric-row">
                <span>Recall %:</span>
                <span className="metric-value">{formatPercentage(data.booking_types?.recall_percentage)}</span>
              </div>
              <div className="metric-row">
                <span>Other Bookings:</span>
                <span className="metric-value">{data.booking_types?.other_bookings || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render recall call analytics
  const renderRecallAnalytics = () => {
    if (!analyticsData?.recall_call_analytics) return null;
    
    const data = analyticsData.recall_call_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Recall Call Performance</h3>
        
        <div className="analytics-grid">
          {/* Call Volume */}
          <div className="analytics-card">
            <h4>Call Volume</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total Calls:</span>
                <span className="metric-value primary">{data.totalRecallCalls || 0}</span>
              </div>
              <div className="metric-row">
                <span>Pickups:</span>
                <span className="metric-value success">{data.pickups || 0}</span>
              </div>
              <div className="metric-row">
                <span>Voicemails:</span>
                <span className="metric-value warning">{data.voicemails || 0}</span>
              </div>
              <div className="metric-row">
                <span>Not Connected:</span>
                <span className="metric-value error">{data.not_connected || 0}</span>
              </div>
            </div>
          </div>
          
          {/* Exact Results */}
          <div className="analytics-card">
            <h4>Call Outcomes</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Bookings Made:</span>
                <span className="metric-value success">{data.exact_results?.bookings_made || 0}</span>
              </div>
              <div className="metric-row">
                <span>Reschedules:</span>
                <span className="metric-value info">{data.exact_results?.reschedules_made || 0}</span>
              </div>
              <div className="metric-row">
                <span>Cancellations:</span>
                <span className="metric-value error">{data.exact_results?.cancellations_made || 0}</span>
              </div>
              <div className="metric-row">
                <span>Notes Taken:</span>
                <span className="metric-value neutral">{data.exact_results?.notes_taken || 0}</span>
              </div>
            </div>
          </div>
          
          {/* Tasks Created */}
          <div className="analytics-card">
            <h4>Tasks & Follow-ups</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total Tasks:</span>
                <span className="metric-value">{data.tasks?.total_tasks_created || 0}</span>
              </div>
              <div className="metric-row">
                <span>High Priority:</span>
                <span className="metric-value error">{data.tasks?.tasks_by_priority?.high || 0}</span>
              </div>
              <div className="metric-row">
                <span>Medium Priority:</span>
                <span className="metric-value warning">{data.tasks?.tasks_by_priority?.medium || 0}</span>
              </div>
              <div className="metric-row">
                <span>Follow-up Texts:</span>
                <span className="metric-value info">{data.tasks?.follow_up_texts || 0}</span>
              </div>
            </div>
          </div>
          
          {/* Judge Analysis */}
          <div className="analytics-card full-width">
            <h4>Call Reasons Analysis</h4>
            <div className="chart-placeholder">
              {Object.entries(data.judge?.reasons || {}).map(([reason, details]) => (
                <div key={reason} className="metric-row">
                  <span>{reason}:</span>
                  <span className="metric-value">{details.count} calls</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render patient outreach analytics
  const renderPatientOutreach = () => {
    if (!analyticsData?.patient_sms_outreach_analytics) return null;
    
    const data = analyticsData.patient_sms_outreach_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Patient SMS Outreach</h3>
        
        <div className="analytics-grid">
          <div className="analytics-card">
            <h4>Outreach Volume</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Conversations Checked:</span>
                <span className="metric-value">{formatNumber(data.total_conversations_checked || 0)}</span>
              </div>
              <div className="metric-row">
                <span>SMS Sent:</span>
                <span className="metric-value">{data.total_sms_sent || 0}</span>
              </div>
              <div className="metric-row">
                <span>Unique Patients:</span>
                <span className="metric-value">{data.unique_patient_names_reached || 0}</span>
              </div>
            </div>
          </div>
          
          <div className="analytics-card">
            <h4>Response Analytics</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Response Rate:</span>
                <span className="metric-value success">{formatPercentage(data.response_rate_percentage)}</span>
              </div>
              <div className="metric-row">
                <span>Patient Responses:</span>
                <span className="metric-value">{data.total_patient_responses_received || 0}</span>
              </div>
              <div className="metric-row">
                <span>Activities Generated:</span>
                <span className="metric-value">{data.total_activities_generated || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render appointment tracking
  const renderAppointmentTracking = () => {
    if (!analyticsData?.recall_no_show_analytics) return null;
    
    const data = analyticsData.recall_no_show_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Appointment Status Tracking</h3>
        
        <div className="analytics-grid">
          <div className="analytics-card">
            <h4>Appointment Status</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total Appointments:</span>
                <span className="metric-value">{data.total_appointments_checked || 0}</span>
              </div>
              <div className="metric-row">
                <span>Pending:</span>
                <span className="metric-value warning">{data.pending_appointments || 0}</span>
              </div>
              <div className="metric-row">
                <span>Confirmed:</span>
                <span className="metric-value success">{data.confirmed_appointments || 0}</span>
              </div>
              <div className="metric-row">
                <span>Completed:</span>
                <span className="metric-value info">{data.completed_appointments || 0}</span>
              </div>
            </div>
          </div>
          
          <div className="analytics-card">
            <h4>No-Show Analysis</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>No-Show Rate:</span>
                <span className="metric-value error">{formatPercentage(data.no_show_rate)}</span>
              </div>
              <div className="metric-row">
                <span>Cancellation Rate:</span>
                <span className="metric-value warning">{formatPercentage(data.cancellation_rate)}</span>
              </div>
              <div className="metric-row">
                <span>Completion Rate:</span>
                <span className="metric-value success">{formatPercentage(data.completion_rate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render callback analytics
  const renderCallbackAnalytics = () => {
    if (!analyticsData?.callback_analytics) return null;
    
    const data = analyticsData.callback_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Callback Analytics</h3>
        
        <div className="analytics-grid">
          <div className="analytics-card">
            <h4>No Pickup Follow-up</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total No Pickup:</span>
                <span className="metric-value warning">{data.no_pickup_analysis?.total_no_pickup || 0}</span>
              </div>
              <div className="metric-row">
                <span>Callbacks Received:</span>
                <span className="metric-value success">{data.no_pickup_analysis?.callbacks_received || 0}</span>
              </div>
              <div className="metric-row">
                <span>Callback Rate:</span>
                <span className="metric-value">{formatPercentage(data.no_pickup_analysis?.callback_rate_percentage || 0)}</span>
              </div>
            </div>
          </div>
          
          <div className="analytics-card">
            <h4>Book Later Follow-up</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total Book Later:</span>
                <span className="metric-value info">{data.book_later_analysis?.total_book_later || 0}</span>
              </div>
              <div className="metric-row">
                <span>Callbacks Received:</span>
                <span className="metric-value success">{data.book_later_analysis?.callbacks_received || 0}</span>
              </div>
              <div className="metric-row">
                <span>Callback Rate:</span>
                <span className="metric-value">{formatPercentage(data.book_later_analysis?.callback_rate_percentage || 0)}</span>
              </div>
            </div>
          </div>
          
          <div className="analytics-card">
            <h4>Overall Callback Performance</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total Callback Opportunities:</span>
                <span className="metric-value">{(data.no_pickup_analysis?.total_no_pickup || 0) + (data.book_later_analysis?.total_book_later || 0)}</span>
              </div>
              <div className="metric-row">
                <span>Total Callbacks Received:</span>
                <span className="metric-value success">{(data.no_pickup_analysis?.callbacks_received || 0) + (data.book_later_analysis?.callbacks_received || 0)}</span>
              </div>
              <div className="metric-row">
                <span>Combined Callback Rate:</span>
                <span className="metric-value">{
                  (() => {
                    const totalOpportunities = (data.no_pickup_analysis?.total_no_pickup || 0) + (data.book_later_analysis?.total_book_later || 0);
                    const totalCallbacks = (data.no_pickup_analysis?.callbacks_received || 0) + (data.book_later_analysis?.callbacks_received || 0);
                    return totalOpportunities > 0 ? formatPercentage(Number(((totalCallbacks / totalOpportunities) * 100).toFixed(2))) : 'N/A';
                  })()
                }</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render days since last appointment analytics
  const renderDaysSinceLastAppointment = () => {
    if (!analyticsData?.days_since_last_appointment_analytics) return null;
    
    const data = analyticsData.days_since_last_appointment_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Days Since Last Appointment Analysis</h3>
        
        <div className="analytics-grid">
          <div className="analytics-card">
            <h4>Average Days Metrics</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Avg Days Call to Last Appointment:</span>
                <span className="metric-value primary">{Number(data.average_days_call_to_last_appointment || 0).toFixed(2)} days</span>
              </div>
              <div className="metric-row">
                <span>Avg Days Booking to Last Appointment:</span>
                <span className="metric-value success">{Number(data.average_days_booking_to_last_appointment || 0).toFixed(2)} days</span>
              </div>
            </div>
          </div>
          
          <div className="analytics-card">
            <h4>Total Days Metrics</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Total Days Call to Last:</span>
                <span className="metric-value info">{formatNumber(data.total_days_call_to_last || 0)} days</span>
              </div>
              <div className="metric-row">
                <span>Total Days Booking to Last:</span>
                <span className="metric-value warning">{formatNumber(data.total_days_booking_to_last || 0)} days</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render lead time analytics
  const renderLeadTimeAnalytics = () => {
    if (!analyticsData?.booking_lead_time_analytics) return null;
    
    const data = analyticsData.booking_lead_time_analytics;
    
    return (
      <div className="analytics-section">
        <h3 className="section-title">Booking Lead Time Analysis</h3>
        
        <div className="analytics-grid">
          <div className="analytics-card">
            <h4>Lead Time Statistics</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Average:</span>
                <span className="metric-value">{Number(data.mean_lead_time || 0).toFixed(2)} days</span>
              </div>
              <div className="metric-row">
                <span>Median:</span>
                <span className="metric-value">{Number(data.median_lead_time || 0).toFixed(2)} days</span>
              </div>
              <div className="metric-row">
                <span>Min:</span>
                <span className="metric-value">{Number(data.min_lead_time || 0).toFixed(2)} days</span>
              </div>
              <div className="metric-row">
                <span>Max:</span>
                <span className="metric-value">{Number(data.max_lead_time || 0).toFixed(2)} days</span>
              </div>
            </div>
          </div>
          
          <div className="analytics-card">
            <h4>Booking Distribution</h4>
            <div className="chart-placeholder">
              <div className="metric-row">
                <span>Same Day:</span>
                <span className="metric-value">{data.distribution?.same_day || 0}</span>
              </div>
              <div className="metric-row">
                <span>Within Week:</span>
                <span className="metric-value">{data.distribution?.within_week || 0}</span>
              </div>
              <div className="metric-row">
                <span>Within Month:</span>
                <span className="metric-value">{data.distribution?.within_month || 0}</span>
              </div>
              <div className="metric-row">
                <span>Beyond Month:</span>
                <span className="metric-value">{data.distribution?.beyond_month || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tab-content">
      <h1>Outbound Call Analytics</h1>
      
      {/* Date Range Input Form */}
      <div className="analytics-form">
        <h3>Get Analytics Data</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="practice-id">Practice:</label>
            {practicesLoading ? (
              <div className="loading-practices">Loading practices...</div>
            ) : practicesError ? (
              <div className="error-practices">{practicesError}</div>
            ) : (
              <select
                id="practice-id"
                value={practiceId}
                onChange={(e) => setPracticeId(e.target.value)}
                disabled={practices.length === 0}
              >
                <option value="">Select a practice...</option>
                {practices.map((practice) => (
                  <option key={practice.practiceId} value={practice.practiceId}>
                    {practice.practiceName} ({practice.practiceId})
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="form-group">
            <label>Date Range:</label>
            <div className="date-range-picker" ref={datePickerRef}>
              <button 
                type="button" 
                className="date-range-button"
                onClick={openDatePicker}
              >
                📅 {formatDateRange()}
                <span className="dropdown-arrow">▼</span>
              </button>
              
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <div className="date-picker-header">
                    <h4>
                      {selectingStart ? 'Select start date' : 'Select end date'}
                    </h4>
                    <button className="close-button" onClick={() => setShowDatePicker(false)}>✕</button>
                  </div>
                  
                  <div className="quick-ranges">
                    <button onClick={() => setQuickRange(0)} className="quick-range-btn">Today</button>
                    <button onClick={() => setQuickRange(6)} className="quick-range-btn">Last 7 days</button>
                    <button onClick={() => setQuickRange(29)} className="quick-range-btn">Last 30 days</button>
                    <button onClick={() => setQuickRange(89)} className="quick-range-btn">Last 90 days</button>
                  </div>
                  
                  <div className="calendar-container">
                    <div className="calendar-header">
                      <button className="nav-button" onClick={() => navigateMonth(-1)}>
                        ‹
                      </button>
                      <span className="month-year">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button className="nav-button" onClick={() => navigateMonth(1)}>
                        ›
                      </button>
                    </div>
                    
                    <div className="calendar-grid">
                      <div className="weekdays">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="weekday">{day}</div>
                        ))}
                      </div>
                      
                      <div className="days-grid">
                        {getDaysInMonth(currentMonth).map((date, index) => {
                          if (!date) {
                            return <div key={index} className="empty-day"></div>;
                          }
                          
                          const isToday = formatDateToString(date) === formatDateToString(new Date());
                          const isSelected = isStartDate(date) || isEndDate(date);
                          const isInRange = isDateInRange(date);
                          const isHovered = isDateRangeHover(date);
                          const isStart = isStartDate(date);
                          const isEnd = isEndDate(date);
                          
                          return (
                            <button
                              key={date.getDate()}
                              className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''} ${isHovered ? 'hovered' : ''} ${isStart ? 'start-date' : ''} ${isEnd ? 'end-date' : ''}`}
                              onClick={() => handleDateClick(date)}
                              onMouseEnter={() => setHoveredDate(date)}
                              onMouseLeave={() => setHoveredDate(null)}
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="date-picker-footer">
                    <div className="selected-range">
                      {startDate && endDate ? (
                        <span>
                          {formatDateRange()}
                        </span>
                      ) : (
                        <span className="instruction">
                          {selectingStart ? 'Click to select start date' : 'Click to select end date'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button onClick={fetchAnalyticsData} disabled={loading}>
            {loading ? 'Loading...' : 'Get Analytics'}
          </button>
          <button onClick={clearData} disabled={loading}>
            Clear
          </button>
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="error-message">{error}</div>
      )}
      
      {/* Loading Indicator */}
      {loading && (
        <div className="loading-indicator">
          Loading analytics data...
        </div>
      )}
      
      {/* Analytics Data Display */}
      {!loading && analyticsData && (
        <div className="analytics-dashboard">
          {/* Header with key info */}
          <div className="dashboard-header">
            <div className="header-info">
              <h2>Newton AI Analytics Dashboard</h2>
              <p style={{fontSize: '0.95em', opacity: 0.85, margin: '4px 0 16px 0'}}>
                Outbound call performance insights powered by AI
              </p>
              <div className="header-meta">
                <span className="practice-id">Practice: {analyticsData.practiceId}</span>
                <span className="date-info">Date: {analyticsData.date}</span>
                <span className="analysis-time">Updated: {formatTimestamp(analyticsData.analysisTimestamp)}</span>
              </div>
            </div>
          </div>
          
          {/* Summary KPI Cards */}
          {renderSummaryCards()}
          
          {/* Timeline Chart */}
          {renderTimelineChart()}
          
          {/* Analytics Sections */}
          {renderBookingAnalytics()}
          {renderRecallAnalytics()}
          {renderCallbackAnalytics()}
          {renderPatientOutreach()}
          {renderDaysSinceLastAppointment()}
          {renderLeadTimeAnalytics()}
          {renderAppointmentTracking()}
          
          {/* Raw JSON Display (Collapsible) */}
          <div className="raw-data-section">
            <details>
              <summary>
                <h3>View Raw JSON Data</h3>
              </summary>
              <div className="json-container">
                <pre>{JSON.stringify(analyticsData, null, 2)}</pre>
              </div>
            </details>
          </div>
        </div>
      )}
      
      {/* No Data Message */}
      {!loading && !analyticsData && !error && practiceId && startDate && endDate && (
        <div className="no-data">
          <p>No analytics data found for practice "{practiceId}" between "{startDate}" and "{endDate}".</p>
        </div>
      )}
      
      {/* Instructions */}
      {!analyticsData && !loading && !error && (
        <div className="instructions">
          <h3>Outbound Call Analytics</h3>
          <p>Get analytics data for a specific practice and date range from the RecallDailyStats database.</p>
          
          <h4>How to use:</h4>
          <ol>
            <li><strong>Select Practice:</strong> Choose a practice from the dropdown (shows all live practices)</li>
            <li><strong>Select Date Range:</strong> Use the date picker to choose start and end dates (defaults to last 7 days)</li>
            <li><strong>Quick Ranges:</strong> Use preset ranges like "Today", "Last 7 days", etc.</li>
            <li><strong>Click "Get Analytics":</strong> Fetch the data from the database</li>
          </ol>
          
          <p><strong>Note:</strong> Date range defaults to the last 7 days. Select a practice and click "Get Analytics" to view data.</p>
          
          <h4>Date Range Features:</h4>
          <ul>
            <li>📅 Visual date range picker similar to Google Analytics</li>
            <li>⚡ Quick selection buttons for common ranges</li>
            <li>🔒 Validation to ensure start date is before end date</li>
            <li>📊 Currently displays first result from selected range</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default OutboundAnalytics;
